/* eslint-disable no-console */
import { call, put, select, all, take, takeLatest, takeEvery } from 'redux-saga/effects'
import _isNil from 'lodash/isNil'

import { getRegion } from 'models/General/selectors'
import handleError from 'models/handle-error'

import { branchLogOut } from 'services/branch/branchService'
import { intercomShutdown } from 'services/Intercom'

import { canvasImageToBlob } from 'utils/canvas'
import { formatEmailAddress } from 'utils/email'
import { replaceFileNameExtension } from 'utils/file'
import { formatPhoneNumber } from 'utils/phone-number'

import actions from './actions'
import api from './api'
import { PROFILE_PROPERTIES } from './constants'
import { getUserId } from './selectors'
import { PROFILE_PROPERTY_CONVERTERS } from './utils'

export function* fetchProfileInfo({ payload }) {
  const { callback } = payload
  let isSuccess = false
  try {
    const result = yield call(api.fetchProfileInfo)
    yield put(actions.fetchProfileInfo.success(result))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] fetchProfileInfo:', error)
    yield put(handleError(error))
    yield put(actions.fetchProfileInfo.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* fetchReferralData({ payload }) {
  const { callback } = payload
  let isSuccess = false

  try {
    const result = yield call(api.fetchReferralData)
    yield put(actions.fetchReferralData.success(result))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] fetchReferralData:', error)
    yield put(handleError(error))
    yield put(actions.fetchReferralData.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* setReklaimRewardsTag({ payload }) {
  const { callback } = payload
  let isSuccess = false
  try {
    yield call(api.setReklaimRewardsTag)
    isSuccess = true
  } catch (err) {
    console.error('[ProfileInfo] setReklaimRewardsTag', err)
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* setIsProfileRewardsMember({ payload }) {
  const { isProfileRewardsMember, callback } = payload
  const userId = yield select(getUserId)
  const payloadData = {
    is_profile_rewards_member: isProfileRewardsMember,
  }
  let isSuccess = false

  try {
    const response = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.setIsProfileRewardsMember.success(response))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setIsProfileRewardsMember:', error)
    yield put(actions.setIsProfileRewardsMember.failure())
  }
  if (callback) {
    yield call(callback, isSuccess)
  }
}

export function* updateAccountField({ payload }) {
  const { key, value: newValue, callback } = payload
  let isSuccess = false
  try {
    const userId = yield select(getUserId)
    const { apiFieldKey } = PROFILE_PROPERTIES[key]
    const converter = PROFILE_PROPERTY_CONVERTERS[key]
    const value = !(_isNil(newValue) || newValue === '') ? converter(newValue) : null
    const payloadData = { [apiFieldKey]: value }
    const result = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.updateAccountField.success(result))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] updateAccountField:', error)
    yield put(handleError(error))
    yield put(actions.updateAccountField.failure())
  }
  if (callback) {
    yield call(callback, isSuccess)
  }
}

export function* addPhoneNumberSendSmsCode({ payload }) {
  const { phoneNumber, callback } = payload
  let isSuccess = false
  let errorCode = null
  try {
    const region = yield select(getRegion)
    const formattedPhoneNumber = yield call(formatPhoneNumber, phoneNumber, region, true)

    yield call(api.addPhoneNumberSendSmsCode, formattedPhoneNumber)
    yield put(actions.addPhoneNumberSendSmsCode.success())
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] addPhoneNumberSendSmsCode:', error)
    errorCode = error?.response?.data?.code ?? null
    yield put(actions.addPhoneNumberSendSmsCode.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess, errorCode)
    }
  }
}

export function* addPhoneNumberVerifySmsCode({ payload }) {
  const { phoneNumber, code, callback } = payload
  let isSuccess = false
  let hasUnveilProfileToScan = false
  let errorCode = null
  try {
    const region = yield select(getRegion)
    const formattedPhoneNumber = yield call(formatPhoneNumber, phoneNumber, region, true)

    const result = yield call(api.addPhoneNumberVerifySmsCode, formattedPhoneNumber, code)
    const { hasUnveilProfile } = result
    hasUnveilProfileToScan = hasUnveilProfile
    yield put(actions.addPhoneNumberVerifySmsCode.success(result))
    yield put(actions.fetchProfileInfo.init())
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] addPhoneNumberVerifySmsCode:', error)
    errorCode = error?.response?.data?.code ?? null
    yield put(actions.addPhoneNumberVerifySmsCode.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess, hasUnveilProfileToScan, errorCode)
    }
  }
}

export function* deleteAccount({ payload }) {
  const { callback } = payload

  let isSuccess = false
  try {
    const userId = yield select(getUserId)
    yield call(api.deleteAccount, userId)
    yield put(actions.deleteAccount.success())
    yield call(intercomShutdown)
    yield call(branchLogOut)
    yield put({ type: 'RESET' })
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] deleteAccount:', error)
    yield put(actions.deleteAccount.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* removeEmailAddress({ payload }) {
  const { emailAddress, callback } = payload

  let isSuccess = false
  let errorCode = null
  try {
    const formattedEmailAddress = yield call(formatEmailAddress, emailAddress)
    yield call(api.removeEmailAddress, formattedEmailAddress)
    yield put(actions.removeEmailAddress.success())
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] removeEmailAddress:', error)
    errorCode = error?.response?.data?.code ?? null
    yield put(actions.removeEmailAddress.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess, errorCode)
    }
  }
}

export function* removePhoneNumber({ payload }) {
  const { phoneNumber, callback } = payload
  let isSuccess = false
  let errorCode = null
  try {
    const region = yield select(getRegion)
    const formattedPhoneNumber = yield call(formatPhoneNumber, phoneNumber, region, true)
    yield call(api.removePhoneNumber, formattedPhoneNumber)
    yield put(actions.removePhoneNumber.success())
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] removePhoneNumber:', error)
    errorCode = error?.response?.data?.code ?? null
    yield put(actions.removePhoneNumber.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess, errorCode)
    }
  }
}

export function* setSellProfileData({ payload }) {
  const { sellProfileData, callback } = payload
  let isSuccess = false
  try {
    const userId = yield select(getUserId)
    const payloadData = { sell_profile_data: sellProfileData }
    const result = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.setSellProfileData.success(result))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setSellProfileData:', error)
    yield put(handleError(error))
    yield put(actions.setSellProfileData.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* setPendingNotificationModalType({ payload }) {
  const { pendingNotificationModalType, callback } = payload
  let isSuccess = false
  try {
    const userId = yield select(getUserId)
    const payloadData = { pending_notification_modal_type: pendingNotificationModalType }
    const result = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.setPendingNotificationModalType.success(result))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setPendingNotificationModalType:', error)
    yield put(handleError(error))
    yield put(actions.setPendingNotificationModalType.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* setIsFinancialDividendMember({ payload }) {
  const { isFinancialDividendMember, callback } = payload
  const userId = yield select(getUserId)
  const payloadData = { is_financial_dividend_member: isFinancialDividendMember }

  let isSuccess = false
  try {
    const response = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.setIsFinancialDividendMember.success(response))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setIsFinancialDividendMember:', error)
    yield put(handleError(error))
    yield put(actions.setIsFinancialDividendMember.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* setIsBrowsingRewardsMember({ payload }) {
  const { isBrowserDividendMember, callback } = payload
  const payloadData = { is_browser_rewards_member: isBrowserDividendMember }

  let isSuccess = false
  try {
    const userId = yield select(getUserId)
    const response = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.setIsBrowsingRewardsMember.success(response))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setIsBrowsingRewardsMember:', error)
    yield put(actions.setIsBrowsingRewardsMember.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* setIsDeviceRewardsMember({ payload }) {
  const { isDeviceRewardsMember, callback } = payload
  const payloadData = { is_device_rewards_member: isDeviceRewardsMember }

  let isSuccess = false
  try {
    const userId = yield select(getUserId)
    const response = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.setIsDeviceRewardsMember.success(response))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setIsDeviceRewardsMember:', error)
    yield put(actions.setIsDeviceRewardsMember.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* setIsUnveilPrompted({ payload }) {
  const { isUnveilPrompted, callback } = payload
  const payloadData = { is_unveil_prompted: isUnveilPrompted }

  let isSuccess = false
  try {
    const userId = yield select(getUserId)
    const response = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.setIsUnveilPrompted.success(response))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setIsUnveilPrompted:', error)
    yield put(actions.setIsUnveilPrompted.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* setEnableSmsNotifications({ payload }) {
  const { isEnabled, callback } = payload
  const userId = yield select(getUserId)
  const payloadData = { enable_sms_notifications: isEnabled }

  let isSuccess = false
  try {
    const response = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.setEnableSmsNotifications.success(response))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setEnableSmsNotifications:', error)
    yield put(handleError(error))
    yield put(actions.setEnableSmsNotifications.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* setEnableEmailNotifications({ payload }) {
  const { isEnabled, callback } = payload
  const userId = yield select(getUserId)
  const payloadData = { enable_email_notifications: isEnabled }

  let isSuccess = false
  try {
    const response = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.setEnableEmailNotifications.success(response))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setEnableEmailNotifications:', error)
    yield put(handleError(error))
    yield put(actions.setEnableEmailNotifications.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* setHasPendingUnveilNotification({ payload }) {
  const { hasPendingUnveilNotification, callback } = payload
  const payloadData = { has_pending_unveil_notification: hasPendingUnveilNotification }

  let isSuccess = false
  try {
    const userId = yield select(getUserId)
    const response = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.setHasPendingUnveilNotification.success(response))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setHasPendingUnveilNotification', error)
    yield put(actions.setHasPendingUnveilNotification.failure(error))
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* setDeviceData({ payload }) {
  const { ipAddress, userAgent, browserLanguage } = payload
  const payloadData = {
    ip_address: ipAddress,
    device_user_agent: userAgent,
    device_language_code: browserLanguage,
  }
  try {
    const userId = yield select(getUserId)
    const response = yield call(api.updateAccount, userId, payloadData)
    yield put(actions.setDeviceData.success(response))
  } catch (error) {
    console.error('[ProfileInfo] setDeviceData:', error)
    yield put(actions.setDeviceData.failure())
  }
}

export function* addEmailAddressSendVerificationEmail({ payload }) {
  const { emailAddress, source, callback } = payload

  let isSuccess = false
  let errorCode = null
  try {
    const response = yield call(api.addEmailAddressSendVerificationEmail, formatEmailAddress(emailAddress), source)
    yield put(actions.addEmailAddressSendVerificationEmail.success(response))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] addEmailAddressSendVerificationEmail:', error)
    errorCode = error?.response?.data?.code ?? null
    yield put(actions.addEmailAddressSendVerificationEmail.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess, errorCode)
    }
  }
}

export function* addEmailAddressVerifyToken({ payload }) {
  const { token, callback } = payload
  let isSuccess = false
  let hasUnveilProfileToScan
  try {
    const result = yield call(api.addEmailAddressVerifyToken, token)
    const { hasUnveilProfile } = result
    hasUnveilProfileToScan = hasUnveilProfile
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] addEmailAddressVerifyToken:', error)
    yield put(actions.addEmailAddressVerifyToken.failure(error))
  }
  if (callback) {
    yield call(callback, isSuccess, hasUnveilProfileToScan)
  }
}

export function* upsertIpAddress({ payload }) {
  const { ipAddress, country, countryRegion, callback } = payload
  let isSuccess = false
  try {
    yield call(api.upsertIpAddress, ipAddress, country, countryRegion)
    isSuccess = true
  } catch (err) {
    console.error('[ProfileInfo] upsertIpAddress', err)
    yield put(actions.upsertIpAddress.failure(err))
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

export function* fetchBrowsingRewardsStatistics({ payload }) {
  const { callback } = payload
  try {
    const response = yield call(api.fetchBrowsingRewardsStatistics)
    yield put(actions.fetchBrowsingRewardsStatistics.success(response.records))
  } catch (error) {
    console.error('[ProfileInfo] fetchBrowsingRewardsStatistics:', error)
    yield put(actions.fetchBrowsingRewardsStatistics.failure())
  } finally {
    if (callback) {
      yield call(callback)
    }
  }
}

export function* setPrimaryEmailAddress({ payload }) {
  const { emailAddress, callback } = payload

  let isSuccess = false
  let errorCode = null

  try {
    yield call(api.setPrimaryEmailAddress, formatEmailAddress(emailAddress))
    yield put(actions.setPrimaryEmailAddress.success())
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setPrimaryEmailAddress:', error)
    errorCode = error?.response?.data?.code ?? null
    yield put(actions.setPrimaryEmailAddress.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess, errorCode)
    }
  }
}

export function* setPrimaryPhoneNumber({ payload }) {
  const { phoneNumber, callback } = payload

  let isSuccess = false
  let errorCode = null

  try {
    const region = yield select(getRegion)
    const formattedPhoneNumber = yield call(formatPhoneNumber, phoneNumber, region, true)
    yield call(api.setPrimaryPhoneNumber, formattedPhoneNumber)
    yield put(actions.setPrimaryPhoneNumber.success())
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setPrimaryPhoneNumber:', error)
    errorCode = error?.response?.data?.code ?? null
    yield put(actions.setPrimaryPhoneNumber.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess, errorCode)
    }
  }
}

export function* setUserHandle({ payload }) {
  const { userHandle, callback } = payload

  let isSuccess = false
  let errorCode = null

  try {
    yield call(api.setUserHandle, userHandle)
    yield put(actions.setUserHandle.success())
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setUserHandle:', error)
    errorCode = error?.response?.data?.error_code ?? null
    yield put(actions.setUserHandle.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess, errorCode)
    }
  }
}

export function* uploadProfilePicture({ payload }) {
  const { profilePictureCanvas, fileName, callback } = payload

  let isSuccess = false
  let errorCode = null

  try {
    const profilePictureBlob = yield call(canvasImageToBlob, profilePictureCanvas, 'image/jpeg', 0.9)
    const uploadFileName = yield call(replaceFileNameExtension, fileName, 'jpg')
    yield call(api.uploadProfilePicture, profilePictureBlob, uploadFileName)
    yield put(actions.uploadProfilePicture.success())
    isSuccess = true
  } catch (error) {
    errorCode = error?.response?.data?.error_code ?? null
    yield put(actions.uploadProfilePicture.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess, errorCode)
    }
  }
}

export function* setIsUnveilOnboarded({ payload }) {
  const { callback } = payload

  let isSuccess = false

  try {
    const response = yield call(api.setIsUnveilOnboarded)
    yield put(actions.setIsUnveilOnboarded.success(response))
    isSuccess = true
  } catch (error) {
    console.error('[ProfileInfo] setIsUnveilOnboarded:', error)
    yield put(actions.setIsUnveilOnboarded.failure())
  } finally {
    if (callback) {
      yield call(callback, isSuccess)
    }
  }
}

function* rootSaga() {
  yield takeEvery(actions.fetchProfileInfo.INIT_TYPE, fetchProfileInfo)
  yield takeLatest(actions.fetchReferralData.INIT_TYPE, fetchReferralData)
  yield takeLatest(actions.setReklaimRewardsTag.INIT_TYPE, setReklaimRewardsTag)
  yield takeLatest(actions.setIsProfileRewardsMember.INIT_TYPE, setIsProfileRewardsMember)
  yield takeLatest(actions.addPhoneNumberSendSmsCode.INIT_TYPE, addPhoneNumberSendSmsCode)
  yield takeLatest(actions.addPhoneNumberVerifySmsCode.INIT_TYPE, addPhoneNumberVerifySmsCode)
  yield takeLatest(actions.deleteAccount.INIT_TYPE, deleteAccount)
  yield takeLatest(actions.removeEmailAddress.INIT_TYPE, removeEmailAddress)
  yield takeLatest(actions.removePhoneNumber.INIT_TYPE, removePhoneNumber)
  yield takeLatest(actions.setSellProfileData.INIT_TYPE, setSellProfileData)
  yield takeLatest(actions.setPendingNotificationModalType.INIT_TYPE, setPendingNotificationModalType)
  yield takeLatest(actions.setIsFinancialDividendMember.INIT_TYPE, setIsFinancialDividendMember)
  yield takeLatest(actions.setIsBrowsingRewardsMember.INIT_TYPE, setIsBrowsingRewardsMember)
  yield takeLatest(actions.setIsDeviceRewardsMember.INIT_TYPE, setIsDeviceRewardsMember)
  yield takeLatest(actions.setEnableSmsNotifications.INIT_TYPE, setEnableSmsNotifications)
  yield takeLatest(actions.setDeviceData.INIT_TYPE, setDeviceData)
  yield takeLatest(actions.setEnableEmailNotifications.INIT_TYPE, setEnableEmailNotifications)
  yield takeLatest(actions.setHasPendingUnveilNotification.INIT_TYPE, setHasPendingUnveilNotification)
  yield takeLatest(actions.setIsUnveilPrompted.INIT_TYPE, setIsUnveilPrompted)
  yield takeLatest(actions.addEmailAddressSendVerificationEmail.INIT_TYPE, addEmailAddressSendVerificationEmail)
  yield takeLatest(actions.addEmailAddressVerifyToken.INIT_TYPE, addEmailAddressVerifyToken)
  yield takeLatest(actions.upsertIpAddress.INIT_TYPE, upsertIpAddress)
  yield takeLatest(actions.fetchBrowsingRewardsStatistics.INIT_TYPE, fetchBrowsingRewardsStatistics)
  yield takeLatest(actions.setPrimaryEmailAddress.INIT_TYPE, setPrimaryEmailAddress)
  yield takeLatest(actions.setPrimaryPhoneNumber.INIT_TYPE, setPrimaryPhoneNumber)
  yield takeLatest(actions.setUserHandle.INIT_TYPE, setUserHandle)
  yield takeLatest(actions.uploadProfilePicture.INIT_TYPE, uploadProfilePicture)
  yield takeLatest(actions.setIsUnveilOnboarded.INIT_TYPE, setIsUnveilOnboarded)
}

function* watchUpdateAccountField() {
  while (true) {
    const updatedData = yield take(actions.updateAccountField.INIT_TYPE)
    yield call(updateAccountField, updatedData)
  }
}

export default function* main() {
  yield all([call(rootSaga), call(watchUpdateAccountField)])
}
